import { useEffect, useState } from "react";
import { Clock, TrendingUp, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "gaonova_search_history";
const MAX_HISTORY = 5;

interface SearchSuggestionsProps {
  onSuggestionClick: (query: string) => void;
}

const trendingSearches = [
  "Terracotta pottery",
  "Madhubani paintings",
  "Wooden crafts",
  "Sabai grass baskets",
  "Dokra art",
];

export const SearchSuggestions = ({ onSuggestionClick }: SearchSuggestionsProps) => {
  const [searchHistory, setSearchHistory] = useState<string[]>([]);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        setSearchHistory(JSON.parse(stored));
      } catch {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  const removeFromHistory = (term: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const updated = searchHistory.filter((t) => t !== term);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    setSearchHistory(updated);
  };

  const clearHistory = () => {
    localStorage.removeItem(STORAGE_KEY);
    setSearchHistory([]);
  };

  return (
    <div className="p-4 space-y-6">
      {/* Recent Searches */}
      {searchHistory.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Recent Searches
            </h3>
            <Button variant="ghost" size="sm" className="text-xs h-auto p-1" onClick={clearHistory}>
              Clear all
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {searchHistory.map((term) => (
              <button
                key={term}
                onClick={() => onSuggestionClick(term)}
                className="group flex items-center gap-1 px-3 py-1.5 bg-muted hover:bg-muted/80 rounded-full text-sm transition-colors"
              >
                {term}
                <X 
                  className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" 
                  onClick={(e) => removeFromHistory(term, e)}
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Trending Searches */}
      <div>
        <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2 mb-3">
          <TrendingUp className="h-4 w-4" />
          Trending Searches
        </h3>
        <div className="flex flex-wrap gap-2">
          {trendingSearches.map((term) => (
            <button
              key={term}
              onClick={() => onSuggestionClick(term)}
              className="px-3 py-1.5 bg-primary/10 hover:bg-primary/20 text-primary rounded-full text-sm transition-colors"
            >
              {term}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Helper function to save search to history
export const saveSearchToHistory = (query: string) => {
  if (!query.trim() || query.length < 2) return;
  
  const stored = localStorage.getItem(STORAGE_KEY);
  let history: string[] = [];
  
  try {
    history = stored ? JSON.parse(stored) : [];
  } catch {
    history = [];
  }
  
  // Remove if exists, add to beginning
  const filtered = history.filter((t) => t.toLowerCase() !== query.toLowerCase());
  const updated = [query, ...filtered].slice(0, MAX_HISTORY);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
};
